import React, { useEffect, useRef, useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { PondsCage } from './components/PondsCage'

import { StackNavigationProp } from '@react-navigation/stack'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { AlertMessage } from '../../components/AlertMessage'
import { DaySelector } from '../../components/DaySelector'
import { FloatingBottomButtons } from '../../components/FloatingBottom/FloatingBottom'
import { PiscicultureCenter } from '../../components/PiscicultureCenter/PiscicultureCenter'
import { TemplatePage } from '../../components/TemplatePage'
import { globalStyle } from '../../theme/global'
import { useKeyboardVisibility } from '../../hooks/useKeyboardVisibility'
import { useForm } from '../../hooks/useForm'
import { MortalityRecord } from '../../context/Alerts/types'
import { MoratlityInfo, MortalityReqBody, MortalityValues } from '../../types'
import { usePost } from '../../hooks/usePost'
import { paths } from '../../api/pathsApi'
import { RoomsModule } from '../../components/RoomsModule/RoomsModule'
import { useSendNotifications } from '../../hooks/useSendNotifications'
import { Alert401 } from '../../components/AlertMessage/Alert401'
import { useContextApp } from '../../hooks/useContextApp'
import { RootParams } from '../../navigator/types'
import { textColor } from '../../theme/colors'

interface NavigateParams extends StackNavigationProp<RootParams, 'Mortality'> {}

export const Mortality = () => {
  const {
    user: { areaSelected },
    handleLogOut,
    handleSaveRecords,
    getTotalPendingRecords,
    deleteRecordStatesAndMortality: deleteRecordByKey,
    freshWaterContext,
    seaWaterContext,
  } = useContextApp()

  useSendNotifications({ pendingRecords: getTotalPendingRecords(areaSelected!) })

  const { t } = useTranslation()
  const navigation = useNavigation<NavigateParams>()
  const route = useRoute<RouteProp<RootParams, 'Mortality'>>()
  const [showAlert, setShowAlert] = useState(false)
  const [error401, setError401] = useState<{ code: number | null; msg: string }>({
    code: null,
    msg: '',
  })

  const registerKey = useRef('')

  const { onChangeValues, values } = useForm<Omit<MortalityValues, 'items'>>({
    levelOneId: route.params?.levelOneId ?? 0,
    levelTwoId: route.params?.levelTwoId ?? 0,
    date: route.params?.date ?? null,
    selectedRecords: route.params?.selectedRecords ?? null,
  })

  const [requestBody, setRequestBody] = useState<MoratlityInfo[]>([])

  const { handleSendData, isLoading, msgError } = usePost<MortalityReqBody[]>({
    url: areaSelected === 'sweetWater' ? paths().MORTALITY.SEND_FRESH : paths().MORTALITY.SEND_SEA,
    onSuccess() {
      navigation.replace('SuccessScreen')
      if (route.params?.selectedRecords) {
        deleteRecordByKey(route.params.selectedRecords, 'mortalityRecords')
      }
    },
    onError(error) {
      if (error.code === 401) {
        handleSaveRecord()
        if (areaSelected === 'sweetWater') {
          freshWaterContext.handleResetData()
        } else {
          seaWaterContext.handleResetData()
        }

        setError401(error)
      }
    },
  })

  const { isKeyboardVisible } = useKeyboardVisibility()

  useEffect(() => {
    registerKey.current = `${values.date}-${values.levelOneId}-${values.levelTwoId}`
  }, [values])

  const handleSaveRecord = () => {
    const milliseconds = Date.now()
    const currentDate = new Date(milliseconds)
    handleSaveRecords<MortalityRecord[]>({
      area: areaSelected,
      key: registerKey.current,
      record: requestBody.map((item) => ({
        ...item,
        dateTime: currentDate.toUTCString(),
        parentKey: `${registerKey.current}`,
        registerKey: `${registerKey.current}-${item.levelThreeId}-${item.mortalityTypeId}`,
        typeMortalityId: item.mortalityTypeId,
        registerDate: item.date,
        totalEntered: item.quantity,
        levelThreeId: item.levelThreeId,
        levelThreeName: item.levelThreeName,
        typeMoratlityName: item.mortalityName,
        levelOneId: values.levelOneId,
        levelTwoId: values.levelTwoId,
      })),
      typeAlert: 'mortalityRecords',
    })
  }
  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={`${t('Formulario Mortalidad')}`}
    >
      <View style={globalStyle.viewContainer}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <PiscicultureCenter
            onChange={(value) => onChangeValues({ value, name: 'levelOneId' })}
            initialValue={values.levelOneId}
          />
          {values.levelOneId !== 0 && (
            <RoomsModule
              parentId={values.levelOneId}
              onChange={(value) => onChangeValues({ value, name: 'levelTwoId' })}
              initialValue={values.levelTwoId}
            />
          )}
          {values.levelTwoId !== 0 && (
            <DaySelector
              style={{ marginTop: 20 }}
              getDate={(value) => onChangeValues({ value, name: 'date' })}
              initialValue={values.date}
            />
          )}
          {values.date !== null && values.levelTwoId !== 0 && (
            <PondsCage
              parentId={values.levelTwoId}
              onChange={(value) => setRequestBody(value)}
              date={values.date}
              initValues={route.params?.items}
            />
          )}
        </ScrollView>
      </View>

      {requestBody.length > 0 && !isKeyboardVisible && (
        <FloatingBottomButtons
          isLoading={isLoading}
          onPressSave={() => {
            handleSaveRecord()
            setShowAlert(true)
          }}
          onPressSend={() => {
            handleSendData(
              requestBody
                .filter((item) => item.quantity > 0)
                .map((item) => ({
                  ...item,
                  date: `${item.date} 00:00`,
                })),
            )
          }}
        />
      )}
      <Alert401 isRender={error401.code === 401} action={handleLogOut} />
      {msgError.length > 0 && (
        <AlertMessage title='ERROR'>
          <Text style={{ textAlign: 'center', color: textColor }}>{msgError}</Text>
        </AlertMessage>
      )}

      {showAlert && (
        <AlertMessage
          title={t('Guardado')}
          action={() => {
            setShowAlert(false)
          }}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('El registro se guardó con éxito')}
          </Text>
        </AlertMessage>
      )}
    </TemplatePage>
  )
}
